import NiceModal from "@ebay/nice-modal-react";
import AccountCreationStep1Modal, {
  type AccountCreationStep1Choice
} from "../../components/modal/AccountCreationStep1/AccountCreationStep1Modal";
import PlanOptionModal, {
  type PlanSelection
} from "../../components/modal/PlanOption/PlanOptionModal";
import { buildPathWithTrackingId } from "../routing/routingUtils";
import { JETPACK_CONFIG, SUBSCRIPTION } from "../routes/routes";
import { tt } from "../i18n/i18n";
import { createNewVenue } from "../../api/createNewVenue";
import {
  addManagedAccount,
  updateCustomerThunk
} from "../../redux/actions/user";
import { setError, setLoading } from "../../redux/actions/app";
import { SubscriptionPlanType } from "@tootsweet/model/datatransfer/SubscriptionPlanDTO";
import PostPaymentModal from "../../components/modal/ConfirmPayment/PostPaymentModal";

export async function startAccountCreationFlow(
  masterToken: string,
  dispatch: any,
  history: any
) {
  try {
    // Step 1: Ask if same legal entity as existing licence
    const step1Choice: AccountCreationStep1Choice | null = await NiceModal.show(
      AccountCreationStep1Modal
    );

    if (!step1Choice) {
      return null;
    }

    // Step 2: Select plan (PlanOptionModal returns { plan, commitment })
    const planSelection: PlanSelection | null = await NiceModal.show(
      PlanOptionModal,
      {
        isForNewVenue: true,
        customTitle: tt("new_venue_add"),
        hideEnterprise: true,
        history
      }
    );

    if (!planSelection) {
      return null;
    }

    const { plan, commitment } = planSelection;

    // Step 3: Create the new venue with empty name and legal entity info
    try {
      dispatch(setLoading(true));

      const newManagedAccount = await createNewVenue(
        {
          placeName: "",
          useSamePaymentMethod: step1Choice.isSameLegalEntity,
          copyFromAccountId: step1Choice.selectedAccount?.trackingID
        },
        masterToken
      );

      // Add the newly created account to Redux store
      dispatch(addManagedAccount(newManagedAccount));

      dispatch(setLoading(false));

      // Step 4: Handle FREE plan differently
      if (plan.type === SubscriptionPlanType.FREE) {
        // Set the isSaasFreeLicense flag for free plans
        dispatch(
          updateCustomerThunk(
            { isSaasFreeLicense: true },
            newManagedAccount.trackingID,
            masterToken
          )
        );

        // For FREE plan, show success modal instead of redirecting to checkout
        const confirmed = await NiceModal.show(PostPaymentModal, {
          headerText: tt("jetpack_checkout_form_success_header"),
          bodyText: tt("jetpack_checkout_form_success_body"),
          buttonText: tt("jetpack_checkout_form_success_button", {
            planName: "gratis"
          })
        });

        if (confirmed) {
          history.push(
            buildPathWithTrackingId(
              JETPACK_CONFIG,
              newManagedAccount.trackingID
            )
          );
        }

        return newManagedAccount;
      }

      // Step 4: Redirect to subscription page with isNew and so parameters
      const subscriptionPath = buildPathWithTrackingId(
        SUBSCRIPTION,
        newManagedAccount.trackingID || ""
      );

      // Add query parameters for plan, duration, isNew flag, and so (skip onboarding) flag
      const searchParams = new URLSearchParams({
        planId: plan._id || "",
        duration: commitment,
        isNew: "true",
        so: "true"
      });

      history.push(`${subscriptionPath}?${searchParams.toString()}`);

      return newManagedAccount;
    } catch (error) {
      console.error("Failed to create new venue:", error);
      dispatch(setLoading(false));
      dispatch(setError(tt("error_creating_venue")));
      return null;
    }
  } catch (error) {
    console.error("Venue creation flow error:", error);
    return null;
  }
}
